import React from 'react';

import { useCurrentProject } from '../../../Configs/CurrentProjectContext';
import { useAppState, useMe } from '../../../Configs/PolStore';
import { Logger } from '../../../Errors/Logger';
import { wrapError } from '../../../Errors/PolErrorHandler';
import { ProductHelper } from '../../../Helpers/ProductHelper';
import { QuoteQuestionUtils } from '../../../Helpers/QuoteQuestionUtils';
import { EstimationUrlParameters, UrlHelper } from '../../../Helpers/UrlHelper';
import { useWindowLog } from '../../../Hooks/WindowLog';
import { I18n } from '../../../Locales/I18nService';
import { QuestionStep } from '../../../Models/Questions/QuestionDto';
import { MapContextState, useMapContext } from '../../../Plugins/FloorPlan/Context/MapContext';
import { MapError, MapHelper } from '../../../Plugins/FloorPlan/Helpers/MapHelper';
import { LogLevelType } from '../../../Services/Analytics/LogLevelType';
import { ApiError } from '../../../Services/XHR/ApiError';
import { RouteQueryParams } from '../../../Types/RouteParams';
import { useQuery } from '../../Hooks/URL/URLUtils';
import { useIsVendeur } from '../../Hooks/Vendeur/useVendeur';
import { EnhancedViewState, useViewState } from '../../Hooks/ViewState/ViewState';
import { NavigationService } from '../../Services/NavigationService';
import { Toast } from '../../Services/ToastService';
import { useGtmMapEventController } from './Controllers/GtmEventController';
import { useQuoteLoadController } from './Controllers/QuoteLoadController';
import { QuestionPrepare } from './Helpers/QuestionPrepare';
import { VersionLoader } from './Helpers/VersionLoader';
import { LoadingStep, QuoteState } from './QuoteState';
import { getProduitPrincipal, toUpdateGeneralQuestions, useUpdateMapItemQuestions } from './Utils/UpdateQuestions';
import { LameFlooringLayout } from 'Plugins/FloorPlan/Components/Laizes/Laize';
import { QuestionContextUtils } from './Utils/QuestionContextUtils';
import { CreateMapItemContextParams } from './Utils/QuestionContextUtils';
import { IRoom, RoomType } from 'Plugins/FloorPlan/Models/IRoom';
import { getMostPerpendicularWall } from 'Plugins/FloorPlan/Helpers/panneauMural.helper';

type UpdateQuoteParams = {
    quoteValues?: EnhancedViewState<QuoteState>;
    mapValues?: EnhancedViewState<MapContextState>;
    mapModified?: boolean;
    refreshLaizeCalc?: boolean;
    questionsModified?: boolean; //!\ can be quote question on MapItem info

    logs?: any;
};
export interface QuoteContextValue {
    state: EnhancedViewState<QuoteState>;
    getState: () => EnhancedViewState<QuoteState>;
    updateQuote: (values: Partial<QuoteState>) => void;
    update: (params: UpdateQuoteParams) => void;

    openFinDeParcours: () => void;
}

const QuoteContext = React.createContext<QuoteContextValue>({
    state: {},
    getState: () => ({}),
    updateQuote: () => {},
    update: () => {},
    openFinDeParcours: () => {},
});
export const useQuoteContext = () => React.useContext(QuoteContext);
export const useQuoteContextState = () => useQuoteContext().state;
export const useVersionId = () => React.useContext(QuoteContext).state.versionId;

export const QuoteProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const map = useMapContext();
    const isVendeur = useIsVendeur();
    const currentProject = useCurrentProject();
    const { version: versionId, ref: produitRef, configId } = useQuery<RouteQueryParams>();
    const { toUpdateMapItemQuestions } = useUpdateMapItemQuestions();

    const quoteContext = useViewState<QuoteState>({ loadingStep: LoadingStep.Auth });
    useWindowLog(() => (window.QuoteState = quoteContext.getState));

    const loader = useQuoteLoadController({ updateQuote: quoteContext.update });
    useGtmMapEventController(map.state, quoteContext.state);

    const load = () => {
        wrapError(
            async () => {
                const state = quoteContext.getState();
                if (!state.initialized) {
                    quoteContext.update({ loadingStep: LoadingStep.Version, loading: false });
                    const { version, versionData } = await VersionLoader.loadVersion(versionId);
                    NavigationService.replaceParams(
                        UrlHelper.toQueryParams({ version: version!, ref: produitRef, configId })
                    );

                    const questions = QuoteQuestionUtils.filterSellersQuestions(versionData.questions, isVendeur);
                    if (configId) {
                        quoteContext.update({ loadingStep: LoadingStep.Configuration });
                        const {
                            mapState,
                            stepHabitation,
                            stepServices,
                            stepSynthese,
                            habitationQuestionsOpen,
                            projetStoreLead,
                            projetStoreDelivery,
                        } = await loader.loadConfiguration({ configId, questions });

                        quoteContext.update({
                            versionId: version,
                            loadingStep: undefined,
                            versionData,
                            questions,
                            stepHabitation,
                            stepServices,
                            stepSynthese,
                            projetStoreLead,
                            projetStoreDelivery,
                            habitationQuestionsOpen,
                            initialized: true,
                        });
                        map.update({ values: mapState, logs: { event: 'load plan from config' } });
                        map.zoomToFit();
                    } else {
                        const stepHabitation = QuestionPrepare.createStepQuestions(
                            questions,
                            QuestionStep.StepHabitation
                        );
                        const stepServices = QuestionPrepare.createStepQuestions(questions, QuestionStep.StepServices);
                        const stepSynthese = QuestionPrepare.createStepQuestions(questions, QuestionStep.StepSynthese);

                        currentProject.reset();
                        quoteContext.update({
                            versionId: version,
                            loadingStep: undefined,
                            versionData,
                            questions,
                            stepHabitation,
                            stepServices,
                            stepSynthese,
                            habitationQuestionsOpen: true,
                            loading: false,
                            initialized: true,
                        });
                    }
                }
            },
            {
                errorHandler: (loadError) => {
                    quoteContext.update({ loadingStep: undefined });
                    Logger.error('QUOTE initialization error', loadError);
                    if ((loadError as ApiError).isApiError) {
                        Toast.showError({
                            title: I18n.get('Toast_TitleError'),
                            content: I18n.get('COMMON_ERROR_MESSAGE'),
                            duration: 5000,
                        });
                        return Promise.resolve(true);
                    }
                    if ((loadError as MapError).isMapError) {
                        const mapError = loadError as MapError;
                        Logger.logAnalytics({
                            message: MapHelper.LOAD_MAP_ERROR_MESSAGE,
                            level: LogLevelType.Error,
                            value: mapError.error,
                        });
                        Toast.showError({
                            title: I18n.get('Toast_TitleError'),
                            content: I18n.get('ERROR_LOADING_MAP'),
                            duration: 5000,
                        });
                        return Promise.resolve(true);
                    }
                    return Promise.resolve(false);
                },
            }
        );
    };

    const update = (params: UpdateQuoteParams) => {
        let { quoteValues, mapValues, questionsModified, refreshLaizeCalc, mapModified, logs } = params;
        let mapState: Partial<MapContextState> = { ...map.getState(), ...mapValues };
        Logger.log('QuoteProvider -> update', params);

        //!\ WHEN UPDATE habitations/question produit principale
        if (quoteValues) {
            const quoteState = quoteContext.getState();
            mapState = map.getState();
            const nextQuoteState = { ...quoteState, ...quoteValues };

            if (mapState.showFinDeParcours) {
                quoteContext.update(toUpdateGeneralQuestions(mapState, nextQuoteState, isVendeur));
            } else {
                quoteContext.update(nextQuoteState);
            }
        }

        if (mapValues) {
            //!\ OCCURS WHEN UPDATE PRODUIT PRINCIPAL OR HABITATION QUESTIONS OR MAP_ITEM INFO CHANGES
            if (questionsModified || mapModified || refreshLaizeCalc) {
                const quoteState = quoteContext.getState();
                const {
                    laizeProps,
                    refreshLaizeCalcNeeded,
                    logs: refreshLaizeCalcLogs,
                } = isRefreshLaizeCalcNeeded(mapState, quoteState, logs);
                const needComputeLaizeUpdate = Boolean(mapModified || refreshLaizeCalc || refreshLaizeCalcNeeded);
                mapState = needComputeLaizeUpdate ? map.withComputeLaize(mapState) : mapState;
                const { modified, ...updatedMapValues } = toUpdateMapItemQuestions({ mapState, quoteState, isVendeur });
                const nextMapState = { ...mapState, ...updatedMapValues, laizeProps };

                map.update({
                    values: nextMapState,
                    logs: { ...refreshLaizeCalcLogs, mapValues, questionsModified },
                });
                if (mapState.showFinDeParcours) {
                    quoteContext.update(toUpdateGeneralQuestions(nextMapState, quoteState, isVendeur));
                }
            } else {
                map.update({
                    values: mapValues,
                    logs: { ...logs, combineWith: 'QuoteProvider update', mapValues, questionsModified },
                });
            }
        }

        if (!currentProject.getState().isDirty && (mapModified || questionsModified)) {
            currentProject.update({ isDirty: true });
        }
    };

    const openFinDeParcours = () => {
        const mapState: Partial<MapContextState> = map.getState();
        const quoteState = quoteContext.getState();
        quoteContext.update(toUpdateGeneralQuestions(mapState, quoteState, isVendeur), () => {
            map.setPanel({ values: { showFinDeParcours: true } });
        });
    };

    const combineMapState = (mapState: Partial<MapContextState>): Partial<MapContextState> => {
        if (!currentProject.getState().isDirty) {
            currentProject.update({ isDirty: true });
        }
        //!\ WHEN UPDATE rooms, openings, roomItems from PANELS or map gestures
        mapState = map.withComputeLaize(mapState);
        mapState.rooms?.forEach((r) => fixRoomLameFlooringLayout(r, mapState));
        const quoteState = quoteContext.getState();
        const { modified, ...updatedMapValues } = toUpdateMapItemQuestions({ mapState, quoteState, isVendeur });
        return { ...mapState, ...updatedMapValues, showFinDeParcours: false };
    };

    React.useEffect(() => map.setStateCombiner({ merge: combineMapState }), [map, combineMapState]);

    useReady(load);
    useLoadProduitFromRef(versionId, produitRef, quoteContext.state, update);
    useInvalidQuoteIfNeeded(quoteContext.state, quoteContext.update);

    return (
        <QuoteContext.Provider
            value={{
                state: quoteContext.state,
                getState: quoteContext.getState,
                updateQuote: quoteContext.update,
                update,
                openFinDeParcours,
            }}>
            {children}
        </QuoteContext.Provider>
    );
};

const useReady = (load: VoidFunction) => {
    //* So we wait until the MeState is ready to load versionData.
    const isAuthenticated = useAppState().Authentication?.isAuthenticated;
    const isMeInitialized = useMe()?.data;

    const isMeReady = !isAuthenticated || (isAuthenticated && isMeInitialized);
    React.useEffect(() => {
        if (isMeReady) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMeReady]);

    return isMeReady;
};

const useLoadProduitFromRef = (
    versionId: string,
    produitRef: string,
    state: EnhancedViewState<QuoteState>,
    update: (params: UpdateQuoteParams) => void
) => {
    React.useEffect(() => {
        if (produitRef && state.initialized) {
            update({ quoteValues: { produitRef }, logs: { event: 'from useLoadProduitFromRef', produitRef } });
            NavigationService.replaceParams(UrlHelper.toQueryParams<EstimationUrlParameters>({ version: versionId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [produitRef, state.initialized]);
};

const useInvalidQuoteIfNeeded = (state: Partial<QuoteState>, updateQuote: (values: Partial<QuoteState>) => void) => {
    const currentProject = useCurrentProject();
    React.useEffect(() => {
        if (state.quote && currentProject.state?.isDirty) {
            updateQuote({ quote: undefined });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.quote, currentProject.state?.isDirty]);
};

const isRefreshLaizeCalcNeeded = (mapState: Partial<MapContextState>, quoteState: Partial<QuoteState>, logs?: any) => {
    //!\ call computelaize if any produitPrincipal changes
    let refreshLaizeCalcNeeded = false;
    let { laizeProps = [], global_surface_area } = mapState;

    mapState.rooms?.forEach((item) => {
        const createContextParams: CreateMapItemContextParams = { global_surface_area };
        QuestionPrepare.prepareStepsQuestions(item, {
            ...createContextParams,
            stepHabitation: quoteState.stepHabitation,
            stopAfterStepProduit: true,
        });
    });

    mapState.rooms?.forEach((room) => {
        const { produitPrincipal } = getProduitPrincipal(room);
        const laizeProps = mapState.laizeProps?.find((x) => x.roomIds?.some((y) => y === room.roomId));

        const remonteesPlinthesMarginCm = ProductHelper.getEvaluationsFromSourceQuestion(room, 'RMT_PLINTHES');
        QuestionContextUtils.getLastStepContext(room, QuestionStep.StepHabitation);
        if (laizeProps) {
            laizeProps.roomIds?.push(room.roomId!);
            if (laizeProps.remonteesPlinthesMarginCm !== remonteesPlinthesMarginCm) {
                laizeProps.remonteesPlinthesMarginCm = remonteesPlinthesMarginCm;
                refreshLaizeCalcNeeded = true;
            }
        }

        if (produitPrincipal?.code !== laizeProps?.productCode) {
            if (!ProductHelper.isLameProduct(produitPrincipal)) {
                room.lameFlooringLayout = undefined;
            }
            refreshLaizeCalcNeeded = true;
        }
    });

    if (refreshLaizeCalcNeeded) {
        laizeProps = ProductHelper.ToArrayLaizeProps(mapState.rooms);
        logs = { ...logs, logLaizeProps: `laizeProps updated`, laizeProps };
    }

    Logger.log('refreshLaizeCalc', { mapState, refreshLaizeCalcNeeded, laizeProps });
    return { laizeProps, refreshLaizeCalcNeeded, logs };
};

const fixRoomLameFlooringLayout = (room: IRoom, mapState: Partial<MapContextState>) => {
    const laizeResult = mapState.laizeResults?.find((x) => x.roomIds?.some((r) => r === room.roomId));
    const canLayLamePleine = laizeResult?.possibleFlooringWallStartByRoomId
        ?.find((item) => item.roomId === room.roomId!)
        ?.value.some((x) => (x.laizeResult ?? 0) > 0);

    Logger.log('fixRoomLameFlooringLayout', { room, canLayLamePleine, laizeResult });
    if (!canLayLamePleine && ProductHelper.isLameProduct(laizeResult?.laizeProps?.produit)) {
        room.lameFlooringLayout = LameFlooringLayout.ClassiqueVertical;
    }
    const updateFlooringWallStart =
        room.type === RoomType.Wall &&
        ProductHelper.isPanneauMural(laizeResult?.laizeProps?.produit) &&
        !room.flooringWallStartValidated;

    const perpendicularWall = getMostPerpendicularWall(room.walls);
    if (updateFlooringWallStart) {
        room.flooringWallStart = perpendicularWall ?? room.flooringWallStart;
    }
};
